/**
 * [polecenie właściciela 2026-09-17, obszar 5] DWIE PIERWSZE LINIJKI KOMUNIKATU — LICZONE Z TEKSTU.
 *
 * Właściciel: „W pasku wyświetlaj tytuł, kategorię oraz 2 pierwsze linijki z opcją kliknięcia
 * do otwarcia w oknie modal."
 *
 * <b>Dlaczego to nie jest `slice(0, 160)` ani `-webkit-line-clamp: 2`.</b> Treść komunikatu jest
 * HTML-em z edytora (`EdytorTresci`), a „linijka" w rozumieniu człowieka to AKAPIT albo złamanie
 * wiersza — nie tyle znaków, ile się zmieści w pasku. Pasek stoi na pełnej szerokości aplikacji
 * i przy tej samej treści na monitorze 27" pokazywałby ZUPEŁNIE inny wycinek niż na laptopie:
 *   • `slice` na HTML-u urywa w środku znacznika i wpuszcza do paska `<p>Uwag`,
 *   • `line-clamp` liczy WIERSZE PO ZŁAMANIU, więc „2 linijki" znaczyłoby coś innego na każdym ekranie,
 *     a na wąskim jeden akapit potrafi zająć cztery wiersze i drugiego akapitu nie widać wcale.
 * Dlatego linia = granica bloku w HTML-u, a przycięcie do dwóch robi się na TEKŚCIE, przed rysowaniem.
 *
 * ⚠️ Znaczniki są ZDEJMOWANE, nie wstawiane. Dekodowanie encji leci przez `textarea.value` —
 * pole, którego zawartość nigdy nie jest interpretowana jako znaczniki (to samo, co robił
 * dotychczasowy `toPlainText` w pasku). Do DOM-u nie wchodzi ani jeden znak HTML-a z komunikatu.
 */

/** Zamknięcia bloków i złamania wiersza = granica linijki. */
const GRANICE_LINII = /<\s*br\s*\/?\s*>|<\s*\/\s*(?:p|div|li|tr|h[1-6]|blockquote|pre|figcaption|td|th)\s*>/gi;

/**
 * Czysty tekst komunikatu z zachowanym podziałem na linie.
 * @param {string|null|undefined} html treść z edytora
 * @returns {string} tekst; linie rozdzielone znakiem nowej linii
 */
export const tekstZHtml = (html) => {
    if (!html) return "";

    const zGranicami = String(html)
        // Skrypt i styl nie są treścią — bez tego ich zawartość wjechałaby do paska jako „tekst".
        .replace(/<\s*(script|style)[\s\S]*?<\s*\/\s*\1\s*>/gi, " ")
        .replace(GRANICE_LINII, "\n")
        .replace(/<[^>]*>/g, " ");

    let rozkodowany = zGranicami;
    if (typeof document !== "undefined") {
        const pole = document.createElement("textarea");
        pole.innerHTML = zGranicami;
        rozkodowany = pole.value;
    }

    return rozkodowany
        // `&nbsp;` z Worda (U+00A0) wygląda jak spacja, ale nie łamie wiersza — stąd dawne zgłoszenie
        // „tekst ucieka w bok". Traktujemy go jak zwykłą spację.
        .replace(/\u00a0/g, " ")
        .split("\n")
        .map((linia) => linia.replace(/[ \t\r\f\v]+/g, " ").trim())
        .filter((linia) => linia.length > 0)
        .join("\n");
};

/**
 * [2026-10-04] Czy treść niesie znaczniki HTML („<” z nazwą znacznika). Treść paska nad nagłówkiem jest od tej
 * daty zwykłym tekstem — a zwykły tekst nie może iść przez zdejmowanie znaczników, bo zdanie
 * „temperatura < 5°C, a wilgotność > 60%” straciłoby wszystko między „<” a „>”.
 */
export const maZnacznikiHtml = (tresc) => /<\/?[a-zA-Z]/.test(String(tresc || ""));

/** Treść jako tekst z podziałem na linijki: HTML → przez zdjęcie znaczników, zwykły tekst → bez ruszania znaków. */
const tekstTresci = (tresc) => {
    if (!tresc) return "";
    if (maZnacznikiHtml(tresc)) return tekstZHtml(tresc);
    return String(tresc)
        .replace(/\u00a0/g, " ")
        .split(/\r?\n/)
        .map((linia) => linia.replace(/[ \t\f\v]+/g, " ").trim())
        .filter((linia) => linia.length > 0)
        .join("\n");
};

/**
 * Pierwsze `ile` linijek tekstu komunikatu.
 * @param {string|null|undefined} html treść z edytora
 * @param {number} [ile=2] ile linijek
 * @returns {string[]} od 0 do `ile` linijek czystego tekstu
 */
export const pierwszeLinieTekstu = (html, ile = 2) => {
    const tekst = tekstTresci(html);
    if (!tekst) return [];
    return tekst.split("\n").slice(0, Math.max(0, ile));
};

/** Czy komunikat ma coś poza pokazanymi linijkami (czyli czy warto zapraszać do otwarcia okna). */
export const maWiecejNizLinie = (html, ile = 2) => {
    const tekst = tekstTresci(html);
    if (!tekst) return false;
    return tekst.split("\n").length > ile;
};
